import React from 'react';
import PropTypes from 'prop-types';
import { Clock } from '@one-thd/sui-icons';
import { Button, useBreakpoint } from '@one-thd/sui-atomic-components';
import classNames from 'classnames';
import helpers from '../../helpers/countdownHelper';

const TimerStackedUnits = ({
  timeStringAndMessaging,
  shouldUseMessagingString,
  shouldIncludeSvg,
  invertText,
  textColor,
  eventIcon,
  eventLinkButton,
  hideIconBelowBreakpoint,
  inspectorProps
}) => {
  const { lesser: isMobile } = useBreakpoint(hideIconBelowBreakpoint || 'lg');

  const { messagingString } = timeStringAndMessaging;
  const {
    hours,
    minutes,
    seconds,
  } = helpers.getTextBelow(timeStringAndMessaging.timeString);

  const labels = {
    days: 'Days',
    hours: 'Hrs',
    minutes: 'Mins',
    seconds: 'Secs'
  };

  const countdown = {};

  if (timeStringAndMessaging.timeString.days !== undefined) {
    countdown.days = timeStringAndMessaging.timeString.days.toString().padStart(2, '0');
  }

  countdown.hours = hours;
  countdown.minutes = minutes;
  countdown.seconds = seconds;

  const boxSize = 'sui-w-[40px] sui-h-[40px] md:sui-w-[44px] md:sui-h-[44px] '
    + 'lg:sui-w-[56px] lg:sui-h-[56px] xl:sui-w-[60px] xl:sui-h-[60px]';
  const numberSize = 'sui-text-xl md:sui-text-2xl lg:sui-text-4xl xl:sui-text-5xl';
  const labelSize = 'sui-text-xs md:sui-text-xs lg:sui-text-sm xl:sui-text-base';

  const elementStyles = {};
  if (textColor) {
    elementStyles.color = textColor;
  }

  let svgColor;
  if (textColor) {
    svgColor = 'current';
  } else {
    svgColor = invertText ? 'inverse' : 'primary';
  }

  const style = {
    // New outer container for icon + message + (countdown+button group)
    outerContainer: classNames(
      'sui-flex',
      'sui-flex-col',
      'md:sui-flex-row',
      'sui-items-center',
      'sui-justify-center',
      'lg:sui-justify-between',
      'sui-gap-4',
      'md:sui-gap-6',
      'lg:sui-gap-8',
      'sui-w-full'
    ),
    // Wrapper for icon + message (grouped together on left)
    iconMessageGroup: classNames(
      'sui-flex',
      'sui-items-center',
      'sui-gap-4 md:sui-gap-6',
      'sui-flex-shrink-0'
    ),
    // Icon wrapper
    leftIconContainer: classNames(
      'sui-flex-none'
    ),
    // Message container
    messageContainer: classNames(
      'sui-flex sui-justify-center sui-items-center',
      'sui-min-w-0'
    ),
    // Wrapper for countdown + button (grouped together on right)
    countdownButtonGroup: classNames(
      'sui-flex',
      'sui-flex-col md:sui-flex-row',
      'sui-items-center',
      'sui-gap-4 md:sui-gap-6',
      'sui-flex-shrink',
      'sui-min-w-0',
      'lg:sui-mr-6'
    ),
    // Button wrapper (right side, close to countdown)
    buttonContainer: classNames(
      'sui-flex-none'
    ),
    numberBox: classNames(
      'sui-flex sui-items-center sui-justify-center',
      'sui-bg-white sui-rounded-lg sui-shadow-md sui-border sui-border-gray-100',
      'sui-h2-bold sui-font-bold',
      boxSize,
      numberSize,
      { 'sui-text-primary': !textColor },
      { 'sui-text-black': invertText && !textColor },
      { 'sui-border-gray-300': invertText && !textColor },
      { 'sui-shadow-lg': invertText && !textColor },
      { 'countdown-dark-box': invertText && !textColor }
    ),
    timeUnitsContainer: classNames(
      'sui-flex',
      'sui-gap-2 md:sui-gap-3 lg:sui-gap-4',
      'sui-flex-shrink',
      'sui-min-w-0'
    ),
    unitContainer: classNames(
      'sui-flex sui-flex-col sui-items-center'
    ),
    iconContainer: classNames(
      'sui-flex sui-items-center sui-justify-center',
      boxSize,
      { 'sui-text-primary': !invertText && !textColor },
      { 'sui-text-inverse': invertText && !textColor },
    )
  };

  const messageTextStyle = classNames(
    'sui-text-center sui-h2-base sui-font-bold',
    'sui-leading-tight sui-tracking-normal',
    'sui-text-3xl md:sui-text-3xl lg:sui-text-4xl xl:sui-text-5xl',
    { 'sui-text-primary': !invertText && !textColor },
    { 'sui-text-inverse': invertText && !textColor },
  );

  const unitLabelStyle = classNames(
    'sui-text-center sui-mt-2',
    'sui-h2-base sui-font-bold sui-leading-tight sui-tracking-normal',
    { 'sui-text-gray-700': !invertText && !textColor },
    { 'sui-text-inverse': invertText && !textColor },
    { 'sui-text-primary': !invertText && !textColor },
    labelSize,
  );

  return (
    <div className={style.outerContainer} data-component="TimerStackedUnits">
      <div className={style.iconMessageGroup}>
        {eventIcon && !isMobile && (
          <div className={style.leftIconContainer}>
            <img
              {...helpers.getDataAttributes(inspectorProps?.icon)}
              src={eventIcon}
              alt="Event Icon"
              className="sui-w-12 sui-h-12 lg:sui-w-16 lg:sui-h-16"
              width="48"
              height="48"
            />
          </div>
        )}

        {messagingString && shouldUseMessagingString && (
          <div className={style.messageContainer}>
            <span
              {...helpers.getDataAttributes(inspectorProps?.message)}
              className={messageTextStyle}
              style={elementStyles}
            >
              {messagingString}
            </span>
          </div>
        )}
      </div>
      <div className={style.countdownButtonGroup}>
        <div {...helpers.getDataAttributes(inspectorProps?.timer)} className={style.timeUnitsContainer}>
          {shouldIncludeSvg && (
            <div className={style.unitContainer}>
              <span className={style.iconContainer} style={elementStyles}>
                <Clock
                  color={svgColor}
                  style={{ width: '100%', height: '100%' }}
                />
              </span>
            </div>
          )}
          {Object.entries(countdown).map(([key, value]) => (
            <div key={key} className={style.unitContainer}>
              <div className={style.numberBox}>
                {value}
              </div>
              <div className={unitLabelStyle} style={elementStyles}>
                {labels[key]}
              </div>
            </div>
          ))}
        </div>
        {eventLinkButton && (
          <div className={style.buttonContainer}>
            <Button
              {...helpers.getDataAttributes(inspectorProps?.button)}
              variant="text"
              href={eventLinkButton.link}
              className={
                'sui-border-b-2 sui-border-b-white sui-cursor-pointer sui-text-inverse '
                + 'sui-whitespace-nowrap sui-px-2 sui-h-10'
              }
            >
              {eventLinkButton.label}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

TimerStackedUnits.displayName = 'TimerStackedUnits';

TimerStackedUnits.propTypes = {
  /**
   * A boolean indicating whether or not you want to lead the timer with "Ends in" or "Ends on"
   */
  shouldUseMessagingString: PropTypes.bool,
  /**
   * To show clock image
   */
  shouldIncludeSvg: PropTypes.bool,
  /**
   * To show message and time
   */
  timeStringAndMessaging: PropTypes.shape({
    messagingString: PropTypes.string,
    timeString: PropTypes.shape({
      days: PropTypes.number,
      hours: PropTypes.number,
      minutes: PropTypes.number,
      seconds: PropTypes.number
    }),
  }).isRequired,
  /**
   * To invert text color for dark backgrounds
   */
  invertText: PropTypes.bool,
  /**
   * Custom text color
   */
  textColor: PropTypes.string,
  /**
   * Optional event icon image URL (hidden below breakpoint)
   */
  eventIcon: PropTypes.string,
  /**
   * Optional event link button configuration
   */
  eventLinkButton: PropTypes.shape({
    label: PropTypes.string,
    link: PropTypes.string,
  }),
  /**
   * Breakpoint below which to hide the event icon (default: 'lg')
   */
  hideIconBelowBreakpoint: PropTypes.string,
  /**
   * Extra data-* attributes for the icon, message, timer and button elements
   */
  inspectorProps: PropTypes.shape({
    icon: PropTypes.shape({}),
    message: PropTypes.shape({}),
    timer: PropTypes.shape({}),
    button: PropTypes.shape({})
  })
};

TimerStackedUnits.defaultProps = {
  shouldUseMessagingString: false,
  shouldIncludeSvg: false,
  invertText: false,
  textColor: '',
  eventIcon: null,
  eventLinkButton: null,
  hideIconBelowBreakpoint: 'lg',
  inspectorProps: null
};

export default TimerStackedUnits;